import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GrayBase, useCounties, project, W, H } from "@/components/broadcast/TvMap";

export function useStormReports() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/storm-reports").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 180_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

// Category → broadcast color + glyph. Tornado reads hottest; flood coolest.
const CAT = {
  tornado: { color: "#d90429", ring: "#ffd60a", label: "Tornado", z: 4 },
  hail:    { color: "#8e44ad", ring: "#ffffff", label: "Hail",    z: 3 },
  wind:    { color: "#f6a21c", ring: "#0a0f1c", label: "Wind",    z: 2 },
  flood:   { color: "#1b9e8a", ring: "#ffffff", label: "Flood",   z: 1 },
};

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
};

export function StormReportsLegend({ byCat = {} }) {
  const order = ["tornado", "hail", "wind", "flood"];
  return (
    <div className="flex items-center gap-4 font-oswald text-[20px] font-bold uppercase tracking-wider text-white" data-testid="storm-reports-legend">
      {order.map((k) => (
        <span key={k} className="flex items-center gap-2">
          <span className="h-5 w-5 rounded-full border-2" style={{ background: CAT[k].color, borderColor: CAT[k].ring }} />
          {CAT[k].label} <span className="opacity-80">{byCat[k] || 0}</span>
        </span>
      ))}
    </div>
  );
}

export function StormReportsSlide({ reports: data }) {
  const counties = useCounties();
  const reps = (data?.reports || []).slice().sort((a, b) => (CAT[a.cat]?.z || 0) - (CAT[b.cat]?.z || 0));
  if (!data?.count) return null;
  const recent = (data.reports || []).slice(0, 7);
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#cfd3d8]" data-testid="storm-reports-slide">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid slice">
        <GrayBase counties={counties}>
          {reps.map((r, i) => {
            const { x, y } = project(r.lat, r.lon);
            const c = CAT[r.cat] || CAT.wind;
            const rad = r.cat === "tornado" ? 18 : 13;
            return (
              <g key={i} data-testid={`lsr-dot-${r.cat}`}>
                <circle cx={x} cy={y} r={rad + 3} fill={c.ring} opacity="0.9" />
                <circle cx={x} cy={y} r={rad} fill={c.color} stroke="#0a0f1c" strokeWidth="2" />
                {r.cat === "tornado" && <circle cx={x} cy={y} r={rad + 9} fill="none" stroke={c.color} strokeWidth="3" opacity="0.7" />}
              </g>
            );
          })}
        </GrayBase>
      </svg>

      {/* Count chip */}
      <div className="absolute right-[24px] top-[170px] flex items-center gap-4 rounded-[3px] bg-white px-5 py-2 font-oswald text-[30px] font-black uppercase tracking-wide text-[#0a0f1c]" data-testid="storm-reports-count">
        <span>{data.count} Report{data.count === 1 ? "" : "s"}</span>
        <span className="rounded-[3px] bg-[#0a0f1c] px-3 py-0.5 text-[22px] text-white">Past {data.window_hours}h</span>
      </div>

      {/* Recent reports ticker list */}
      <div className="absolute bottom-[64px] left-[24px] max-w-[880px] rounded-[6px] bg-[#0a0f1c]/88 p-4 backdrop-blur-sm" data-testid="storm-reports-recent">
        <div className="mb-2 font-oswald text-[19px] font-black uppercase tracking-[0.22em] text-[#ffd60a]">Latest Reports</div>
        <div className="flex flex-col gap-1.5">
          {recent.map((r, i) => {
            const c = CAT[r.cat] || CAT.wind;
            return (
              <div key={i} className="flex items-center gap-3 font-oswald text-white" data-testid={`lsr-row-${i}`}>
                <span className="h-3.5 w-3.5 shrink-0 rounded-full" style={{ background: c.color }} />
                <span className="w-[176px] shrink-0 text-[23px] font-bold uppercase">{r.label}{r.mag ? ` · ${r.mag}` : ""}</span>
                <span className="flex-1 truncate text-[22px] font-semibold text-[#c3ccd8]">{r.city || r.county}{r.county ? ` · ${r.county} Co.` : ""}</span>
                <span className="shrink-0 text-[20px] font-semibold text-[#7e8aa0]">{fmtTime(r.valid)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="absolute bottom-[18px] left-[24px] font-oswald text-[17px] uppercase tracking-widest text-[#0a0f1c]/75">
        {data.source} · spotter ground-truth
      </div>
    </div>
  );
}
